import {ApolloError} from "@apollo/client";
import {t} from "../i18n";
import {Toast} from "antd-mobile";
import {CloseCircleFill} from "antd-mobile-icons";
import {playRepeatedScanSound} from "./scanUtils";

export interface ParseExceptionResult {
    title: string;
    message: string;
}

// 类型守卫函数，用于检查对象是否符合ParseExceptionResult接口
export const isParseExceptionResult = (obj: any): obj is ParseExceptionResult => {
    return obj && typeof obj === 'object' && 
           typeof obj.title === 'string' && 
           typeof obj.message === 'string';
};

// 处理状态翻译的辅助函数
const translateStatusInMessage = (message: string): string => {
    // 定义所有可能的状态值，按长度从长到短排序，避免短状态值作为子串提前匹配
    // 例如 PRE_DELIVERED 包含 DELIVERED，RETURN_ARRIVED_DESTINATION_HUB 包含 ARRIVED_DESTINATION_HUB
    const statusValues = [
        'RETURN_ARRIVED_DESTINATION_HUB',
        'ARRIVED_DESTINATION_HUB', 'ARRIVED_ORIGIN_HUB',
        'WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS', 'DELIVERY_FAILED',
        'PENDING_INTERCEPTION', 'PENDING_SHIPMENT',
        'PRE_DELIVERED', 'DELIVERED',
        'RETURN_STARTED', 'RETURNED',
        'CREATED', 'CANCELLED', 'HANDED_OVER',
        'IN_TRANSIT', 'ARRIVED_WHS',
        'INTERCEPTED', 'HOLDING', 'PICKUP_FAILED',
    ];

    // 检查消息中是否包含任何状态值
    for (const statusValue of statusValues) {
        if (message.includes(statusValue)) {
            const statusKey = `common.overallStatus.${statusValue}`;
            const translatedStatus = t(statusKey);

            // 如果找到了翻译，进行替换
            if (translatedStatus !== statusKey) {
                return message.replace(statusValue, translatedStatus);
            }
        }
    }
    return message;
};

export const parseException = (error?: any): ParseExceptionResult => {


    if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        const details = graphQLError.extensions?.details;
        console.log("messageKey:"+graphQLError.extensions?.messageKey)
        const titleKey = `${graphQLError.extensions?.messageKey}.title`;
        let title = t(titleKey as string, details);
        if (title === titleKey) {
            title = t('errors.title');
        }

        let messageKey = `${graphQLError.extensions?.messageKey}.message`;
        let message = t(messageKey, details);
        if (message === messageKey) {
            messageKey = `${graphQLError.extensions?.messageKey}`;
            message = t(messageKey, details);
        }

        if(graphQLError.extensions?.messageKey == "errors.business.pda.returnScanNotAllowed"
            || graphQLError.extensions?.messageKey == "errors.business.pda.returnToWhsScanNotAllowed") {
            // 处理状态翻译
            message = translateStatusInMessage(message);
        }
        
        return { 
            title: title,
            message: message
        }
    }
    return {
        title: t('errors.title'),
        message: t('errors.system.internal')
    }
};

export const parseExceptionMessageKey = (error?: any): string => {
    if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        let messageKey = `${graphQLError.extensions?.messageKey}`;
        return messageKey
    }
    return  'errors.system.internal'
};

// 存储当前显示的错误Toast
let currentErrorToast: any = null;

export const showError = (error?: any) => {
    const {title, message} = isParseExceptionResult(error) ? error : parseException(error);
    
    // 如果已经有错误Toast在显示，先关闭它
    if (currentErrorToast) {
        currentErrorToast.close();
        currentErrorToast = null;
    }
    
    currentErrorToast = Toast.show({
        content: (
            <div className="relative py-2" style={{ minWidth: '200px', maxWidth: '80vw' }}>
                <div className="flex items-center justify-center mb-2 relative">
                    <div className="flex items-center gap-2">
                        <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                        <div className="text-base">{title}</div>
                    </div>
                    <div
                        className="absolute right-0 cursor-pointer flex items-center justify-center w-6 h-6"
                        onClick={() => {
                            if (currentErrorToast) {
                                currentErrorToast.close();
                                currentErrorToast = null;
                            }
                        }}
                    >
                        <span className="text-white text-lg">✕</span>
                    </div>
                </div>
                <div className="text-sm text-center px-4">{message}</div>
            </div>
        ),
        duration: 0 // 不自动消失，需要用户手动关闭
    });
};

// 清除当前错误Toast的函数
export const clearCurrentError = () => {
    if (currentErrorToast) {
        currentErrorToast.close();
        currentErrorToast = null;
    }
};

// 存储当前显示的警告Toast
let currentWarningToast: any = null;

// 显示警告/注意提示
export const showWarning = (options: { title?: string; message: string }) => {
    const { title = t('errors.warning'), message } = options;

    // 如果已经有警告Toast在显示，先关闭它
    if (currentWarningToast) {
        currentWarningToast.close();
        currentWarningToast = null;
    }

    currentWarningToast = Toast.show({
        content: (
            <div className="relative py-2">
                <div className="flex items-center justify-center mb-2 relative">
                    <div className="flex items-center gap-2">
                        <span className="text-yellow-400" style={{ fontSize: '20px' }}>⚠️</span>
                        <div className="text-base">{title}</div>
                    </div>
                    <div
                        className="absolute right-0 cursor-pointer flex items-center justify-center w-6 h-6"
                        onClick={() => {
                            if (currentWarningToast) {
                                currentWarningToast.close();
                                currentWarningToast = null;
                            }
                        }}
                    >
                        <span className="text-white text-lg">✕</span>
                    </div>
                </div>
                <div className="text-sm text-center px-4">{message}</div>
            </div>
        ),
        duration: 0 // 不自动消失，需要用户手动关闭
    });
};

// 清除当前警告Toast的函数
export const clearCurrentWarning = () => {
    if (currentWarningToast) {
        currentWarningToast.close();
        currentWarningToast = null;
    }
};

export const showErrorNoTitle = (error?: any) => {
    const { message } = parseException(error);
    let showMessage : string;
    switch ( message){
        case t('errors.business.alreadyScanned'):
            showMessage = message;
            // 播放重复扫描声音
            playRepeatedScanSound();
            break;
        case t('errors.business.alreadyFinished'):
            showMessage = message;
            break;
        default:
            showMessage = t('pages.delivery.exception.showMessage');
    }
    Toast.show({
        content: `${showMessage}`,
        icon: <div className="flex items-center gap-2 justify-center">
            <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
        </div>,
        duration: 2000
    });
};

/**
 * 专门处理重复扫描错误的函数
 * 播放重复扫描声音并显示错误信息
 */
export const showRepeatedScanError = (error?: any) => {
    const { message } = parseException(error);
    // 播放重复扫描声音
    playRepeatedScanSound();
    
    Toast.show({
        content: `${message}`,
        icon: <div className="flex items-center gap-2 justify-center">
            <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
        </div>,
        duration: 2000
    });
};

/**
 * 灵活处理错误并显示 Toast
 */
export const showErrorToast = (error?: any) => {
    const messageKey = parseExceptionMessageKey(error);

    if (!messageKey || messageKey === 'errors.system.internal') {
        Toast.show({
            content: t('errors.system.internal'),
        });
        return;
    }

    // 使用完整的 messageKey 获取翻译
    const content = t(messageKey);

    Toast.show({
        content: content,
    });
};
